import React from 'react';
import {Link} from 'react-router-dom';

import {getNwIdSearchString} from '../../../state/workspaces/url';
import {useNavContext} from '../../NavContextProvider';

type SingleRunLinkProps = {
  entityName: string;
  projectName: string;
  runName: string;
  linkText?: string;
};
export function SingleRunLink({
  entityName,
  projectName,
  runName,
  linkText,
}: SingleRunLinkProps) {
  const to = useSingleRunLink({entityName, projectName, runName});
  return (
    <Link to={to} target="_blank" rel="noopener noreferrer">
      {linkText || runName}
    </Link>
  );
}

/**
 * Returns the link to a workspace in single-run mode.
 *
 * - Must maintain the current named workspace URL search param
 * - Must use group or sweep specific run path if user is on a group or sweep workspace
 * - Cross-project runs (e.g. cross-project pinned runs) link to the run's
 *   source project's single run view and do NOT carry over any sweep/group context.
 */
export function useSingleRunLink(args?: {
  entityName: string;
  projectName: string;
  runName: string;
}): string {
  // must `useNavContext` instead of `useParams` because this hook is
  // used in the top nav bar where `useParams` are not populated
  const {params} = useNavContext();

  if (!args) {
    return '';
  }

  const {entityName, projectName, runName} = args;
  let basePath = `/${entityName}/${projectName}/runs/${runName}`;
  // must account for sweep/group names and named workspaces
  const {
    entityName: currentEntityName,
    projectName: currentProjectName,
    groupName,
    sweepName,
  } = params;
  // Only prefix the current sweep/group when the destination run lives in
  // the same project as the current sweep/group workspace
  const isSameProject =
    currentEntityName === entityName && currentProjectName === projectName;
  if (isSameProject && sweepName) {
    basePath = `/${entityName}/${projectName}/sweeps/${sweepName}/runs/${runName}`;
  } else if (isSameProject && groupName) {
    basePath = `/${entityName}/${projectName}/groups/${groupName}/runs/${runName}`;
  }
  return basePath + getNwIdSearchString();
}
